// A linha digitavel de um boleto — a regra vive AQUI porque ela vale nos dois lados
// ([DA-629], 27/08/2026): o servidor recusa o que nao cabe antes de gravar, e o app do
// cliente formata pro olho conferir. Escrita duas vezes, ela divergiria na primeira
// mudanca — e ninguem compararia as duas.
//
// ⚠️ **O que se GUARDA sao os digitos, e so eles.** Cada painel de banco copia com uma
// mascara diferente (pontos, espacos, hifens), e o aplicativo do banco do cliente le
// numero puro. Guardar a mascara faria o "copiar" do app entregar algo que o banco
// dele pode recusar.

/** So os digitos do que foi colado. String vazia vira `null` — campo opcional. */
export function normalizarLinhaDigitavel(bruto: string | null | undefined): string | null {
  const digitos = String(bruto ?? '').replace(/\D/g, '');
  return digitos.length > 0 ? digitos : null;
}

/**
 * Cabe numa linha digitavel de verdade?
 *
 * ⚠️ **Sao DOIS tamanhos, e recusar o segundo seria barrar boleto legitimo**: o de
 * COBRANCA (banco, o caso da loja) tem 47 digitos; o de ARRECADACAO (concessionaria,
 * tributo, o que uma nota de fornecedor pode trazer junto) tem 48. Aceitamos de 44 a
 * 48 porque um digito perdido no copiar-colar e' mais comum que um boleto de tamanho
 * exotico, e o objetivo aqui e' pegar o dedo escorregado — nao validar o codigo de
 * barras, que sem a conferencia dos DVs seria promessa que nao se cumpre.
 *
 * ⛔ **Nao transformar isto num validador de digito verificador.** Recusar por DV
 * exigiria implementar modulo 10/11 por posicao e por tipo de boleto, e errar isso
 * barraria um boleto CERTO na frente do operador — pela regra da casa, trava dura so
 * onde o erro do outro lado e' pior. Aqui o outro lado e' o cliente sem a linha, que
 * ainda tem o PDF.
 */
export function linhaDigitavelCabe(digitos: string | null): boolean {
  if (digitos === null) return true; // campo opcional
  return digitos.length >= 44 && digitos.length <= 48;
}

/**
 * A linha com a mascara que o olho consegue conferir contra o papel.
 *
 * Cobranca (47): `AAAAA.AAAAA BBBBB.BBBBBB CCCCC.CCCCCC D EEEEEEEEEEEEEE`.
 * Arrecadacao (48): quatro blocos de 12.
 *
 * ⚠️ Tamanho fora dos dois volta **como veio** em vez de sair picado errado — mascara
 * torta le como numero errado, e o numero esta certo.
 */
/** Bancos que emitem boleto com mais frequencia. Fora da lista, a tela diz "Banco NNN". */
const BANCOS: Record<string, string> = {
  '001': 'Banco do Brasil', '033': 'Santander', '077': 'Inter', '104': 'Caixa',
  '208': 'BTG Pactual', '237': 'Bradesco', '260': 'Nubank', '336': 'C6 Bank',
  '341': 'Itaú', '403': 'Cora', '422': 'Safra', '748': 'Sicredi', '756': 'Sicoob',
};

export interface DadosDaLinha {
  banco_codigo: string;
  /** Nome conhecido ou "Banco NNN". */
  banco: string;
  /** `null` = boleto sem valor impresso (o pagador digita no banco). */
  valor: number | null;
  /** 'YYYY-MM-DD'; `null` = boleto sem vencimento (fator 0000). */
  vencimento: string | null;
}

/**
 * O que a propria linha de COBRANCA (47 digitos) diz: banco, valor e vencimento.
 *
 * O ultimo bloco (14 digitos) e' `FFFF` (fator de vencimento) + `VVVVVVVVVV` (valor em
 * centavos). ⚠️ **O fator reiniciou em 22/02/2025** (Febraban: 9999 virou 1000), entao
 * a conta usa a base nova — boleto emitido antes disso ja venceu ha mais de um ano.
 * Conferido nos 7 boletos com linha gravados ate 20/10/2026: valor bateu com a soma dos
 * titulos em todos, e o vencimento com o gravado em 6 (o 7o tinha sido ajustado a mao).
 *
 * Arrecadacao (48) e tamanho torto devolvem `null` — a tela entao nao afirma nada.
 */
export function lerLinhaDigitavel(digitos: string | null): DadosDaLinha | null {
  if (!digitos || !/^\d{47}$/.test(digitos)) return null;
  const banco_codigo = digitos.slice(0, 3);
  const fator = Number(digitos.slice(33, 37));
  const centavos = Number(digitos.slice(37, 47));
  let vencimento: string | null = null;
  if (fator >= 1000) {
    const d = new Date(Date.UTC(2025, 1, 22 + (fator - 1000)));
    vencimento = d.toISOString().slice(0, 10);
  }
  return {
    banco_codigo,
    banco: BANCOS[banco_codigo] ?? `Banco ${banco_codigo}`,
    valor: centavos > 0 ? centavos / 100 : null,
    vencimento,
  };
}

export function formatarLinhaDigitavel(digitos: string | null): string {
  if (!digitos) return '';
  if (digitos.length === 48) return (digitos.match(/.{1,12}/g) ?? [digitos]).join(' ');
  if (digitos.length === 47) {
    return digitos.replace(
      /^(\d{5})(\d{5})(\d{5})(\d{6})(\d{5})(\d{6})(\d)(\d{14})$/,
      '$1.$2 $3.$4 $5.$6 $7 $8',
    );
  }
  return digitos;
}
